const WISHLIST_PAGE_KEY = 'wishlist';

let allProducts = [];

// ─── Wishlist storage ────────────────────────────────────────────────────────

function getWishlist() {
  try {
    const data = JSON.parse(localStorage.getItem(WISHLIST_PAGE_KEY)) || [];
    if (data.length > 0 && typeof data[0] === 'string') {
      return data.map((id) => ({ id, handle: '' }));
    }
    return data;
  } catch (e) {
    return [];
  }
}

// ─── Fetch helpers ───────────────────────────────────────────────────────────

async function fetchProductAvailability(handle) {
  try {
    const query = `{ product(handle: "${handle}") { availableForSale } }`;
    const res = await fetch('/api/2025-01/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    });
    if (!res.ok) return undefined;
    const data = await res.json();
    return data.data?.product?.availableForSale;
  } catch (e) {
    return undefined;
  }
}

async function fetchProduct(handle) {
  try {
    const [productData, available] = await Promise.all([
      fetch(`/products/${handle}.json`).then((r) => (r.ok ? r.json() : null)),
      fetchProductAvailability(handle),
    ]);
    if (!productData) return null;
    const product = productData.product;
    if (available !== undefined) product.available = available;
    return product;
  } catch (e) {
    return null;
  }
}

// ─── Utilities ───────────────────────────────────────────────────────────────

function formatPrice(priceStr) {
  return `¥${parseInt(priceStr, 10).toLocaleString('ja-JP')}`;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getProductTags(product) {
  if (Array.isArray(product.tags)) return product.tags;
  return product.tags ? product.tags.split(',').map((t) => t.trim()) : [];
}

// ─── Badge rendering ─────────────────────────────────────────────────────────

function getBadgeHtml(product) {
  const config = document.getElementById('wishlist-badge-config');
  const soldOutColor = config?.dataset.soldOutColor || '3';
  const saleColor = config?.dataset.saleColor || '2';

  // Shopify storefront API does not return available/inventory fields for this store.
  // Only treat as sold-out when available is explicitly boolean false.
  if (product.available === false) {
    return `<span class="badge badge--bottom-left color-${soldOutColor}">売り切れ</span>`;
  }

  const isOnSale = Array.isArray(product.variants) && product.variants.some((v) => {
    const compareAt = parseInt(v.compare_at_price || '0', 10);
    const price = parseInt(v.price || '0', 10);
    return compareAt > price;
  });
  if (isOnSale) {
    return `<span class="badge badge--bottom-left color-${saleColor}">セール</span>`;
  }

  if (getProductTags(product).includes('NEW')) {
    return `<span class="badge badge--bottom-left color-new-badge">NEW</span>`;
  }

  return '';
}

// ─── Card rendering ──────────────────────────────────────────────────────────

const HEART_SVG = `<svg width="18" height="15" viewBox="0 0 18 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.93472 8.41962L8.0186 14.1342C8.2286 14.3311 8.3336 14.43 8.4561 14.4545C8.51186 14.4655 8.56922 14.4655 8.62497 14.4545C8.74922 14.43 8.85335 14.332 9.06247 14.1342L15.1463 8.42049C15.9727 7.64462 16.4799 6.58838 16.5687 5.45837C16.6576 4.32836 16.3217 3.20584 15.6267 2.31037L15.3555 1.96124C13.6335 -0.257759 10.1772 0.114117 8.9671 2.64987C8.9286 2.73023 8.86817 2.79806 8.79277 2.84555C8.71737 2.89303 8.63008 2.91823 8.54097 2.91823C8.45187 2.91823 8.36458 2.89303 8.28918 2.84555C8.21378 2.79806 8.15335 2.73023 8.11485 2.64987C6.90472 0.114117 3.44847 -0.258634 1.72647 1.96124L1.45522 2.31124C0.760784 3.20659 0.425187 4.32873 0.514021 5.45834C0.602856 6.58794 1.10974 7.64382 1.9356 8.41962H1.93472Z" fill="white" stroke="#B2B2B2"/></svg>`;

function renderCard(product) {
  const image1 = product.images[0]?.src || '';
  const image2 = product.images[1]?.src || '';
  const price = product.variants[0]?.price || '0';
  const url = `/products/${product.handle}`;
  const title = escapeHtml(product.title);
  const badgeHtml = getBadgeHtml(product);

  return `
    <li class="grid__item" data-product-id="${product.id}">
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <div class="card card--standard card--media" style="--ratio-percent: 125%;">
          <div class="card__inner color-scheme-2 gradient ratio" style="--ratio-percent: 125%;">
            <div class="card__media">
              <div class="media media--transparent media--hover-effect">
                ${image1 ? `<img src="${image1}" alt="${title}" class="motion-reduce">` : ''}
                ${image2 ? `<img src="${image2}" alt="" class="motion-reduce" loading="lazy">` : ''}
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading">
                  <a href="${url}" class="full-unstyled-link">${title}</a>
                </h3>
              </div>
              <div class="card__badge bottom left">${badgeHtml}</div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <div class="card__heading-row">
                <h3 class="card__heading h5">
                  <a href="${url}" class="full-unstyled-link">${title}</a>
                </h3>
                <button class="wishlist-btn is-active" data-product-id="${product.id}" data-product-handle="${product.handle}" aria-label="お気に入りから削除" aria-pressed="true">
                  ${HEART_SVG}
                </button>
              </div>
              <div class="card-information">
                <div class="price">
                  <div class="price__container">
                    <div class="price__regular">
                      <span class="visually-hidden visually-hidden--inline">通常価格</span>
                      <span class="price-item price-item--regular">${formatPrice(price)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>
  `;
}

// ─── Filter state ────────────────────────────────────────────────────────────

const filterState = {
  availability: new Set(),
  priceRanges: [],  // { gte: number|null, lte: number|null }[] — 選択中の価格帯（複数可）
  tags: new Set(),
  sort: 'manual',
};

function computePriceUnion(ranges) {
  let minGte = null, maxLte = null, hasOpenLow = false, hasOpenHigh = false;
  ranges.forEach((r) => {
    if (r.gte === null) hasOpenLow = true;
    else minGte = minGte === null ? r.gte : Math.min(minGte, r.gte);
    if (r.lte === null) hasOpenHigh = true;
    else maxLte = maxLte === null ? r.lte : Math.max(maxLte, r.lte);
  });
  return { gte: hasOpenLow ? null : minGte, lte: hasOpenHigh ? null : maxLte };
}

function getMaxProductPrice() {
  if (!allProducts.length) return Infinity;
  return allProducts.reduce((max, p) => Math.max(max, parseInt(p.variants[0]?.price || '0', 10)), 0);
}

// SVGs used inside dynamically generated filter HTML
const SVG_CARET_F = `<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/></svg>`;
const SVG_CHECKBOX_F = `<svg width="1.6rem" height="1.6rem" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1"/></svg>`;
const SVG_CHECKMARK_F = `<svg aria-hidden="true" class="icon icon-checkmark" width="1.1rem" height="0.7rem" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 3.5L2.83333 4.75L4.16667 6L9.5 1" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const SVG_CLOSE_SMALL_F = `<svg aria-hidden="true" focusable="false" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none"><path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const SVG_ARROW_F = `<svg viewBox="0 0 14 10" fill="none" aria-hidden="true" focusable="false" class="icon icon-arrow"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"/></svg>`;

// Price range presets — mirrors the "default" (mixed) branch in facets-mobile-filter-groups.liquid
const PRICE_RANGES = [
  { id: 'e1', gte: null,   lte: 5000,   label: '〜¥5,000' },
  { id: 'e2', gte: 5001,   lte: 8000,   label: '¥5,001〜¥8,000' },
  { id: 'e3', gte: 8001,   lte: 11000,  label: '¥8,001〜¥11,000' },
  { id: 'e4', gte: 11001,  lte: 100000, label: '¥11,001〜¥100,000' },
  { id: 'e5', gte: 100001, lte: 199999, label: '¥100,001〜¥199,999' },
  { id: 'e6', gte: 200000, lte: 299999, label: '¥200,000〜¥299,999' },
  { id: 'e7', gte: 300000, lte: 399999, label: '¥300,000〜¥399,999' },
  { id: 'e8', gte: 400000, lte: 499999, label: '¥400,000〜¥499,999' },
  { id: 'e9', gte: 500000, lte: null,   label: '¥500,000〜' },
];

// ─── Tag group config ────────────────────────────────────────────────────────

function getTagGroups() {
  const el = document.getElementById('wishlist-tag-groups-config');
  if (!el) return null;
  function split(str) { return str ? str.split(',').map((s) => s.trim()).filter(Boolean) : []; }
  return {
    dressLine: split(el.dataset.dressLine),
    brand: split(el.dataset.brand),
    size: split(el.dataset.size),
    accessoryType: split(el.dataset.accessoryType),
  };
}

// ─── Filter UI building ──────────────────────────────────────────────────────

function mobileCheckboxItem(id, group, value, label, count) {
  return `
    <li class="mobile-facets__item list-menu__item">
      <label for="${id}" class="facets__label mobile-facets__label">
        <input class="mobile-facets__checkbox" type="checkbox" id="${id}" data-group="${group}" value="${escapeHtml(value)}">
        <span class="mobile-facets__highlight"></span>
        ${SVG_CHECKBOX_F}${SVG_CHECKMARK_F}
        <span class="facet-checkbox__text" aria-hidden="true">${escapeHtml(label)} (${count})</span>
        <span class="visually-hidden">${escapeHtml(label)}</span>
      </label>
    </li>`;
}

function mobileDetails(idx, label, resetGroup, bodyHtml) {
  return `
    <details class="mobile-facets__details js-filter" data-index="mobile-${idx}">
      <summary class="mobile-facets__summary focus-inset">
        <div>
          <span>${escapeHtml(label)}</span>
          <span class="mobile-facets__arrow no-js-hidden">${SVG_ARROW_F}</span>
        </div>
      </summary>
      <div class="mobile-facets__submenu gradient">
        <button class="mobile-facets__close-button link link--text focus-inset" type="button" data-close-submenu>
          ${SVG_ARROW_F}
          <span>${escapeHtml(label)}</span>
        </button>
        ${bodyHtml}
        <div class="no-js-hidden mobile-facets__footer gradient">
          <facet-remove class="mobile-facets__clear-wrapper">
            <a href="#" class="mobile-facets__clear underlined-link" role="button" data-reset="${resetGroup}">クリア</a>
          </facet-remove>
          <button type="button" class="button button--primary" data-close-submenu>適用</button>
        </div>
      </div>
    </details>`;
}

function buildFilters(products) {
  let availableCount = 0;
  let soldOutCount = 0;
  const allTags = new Map();

  products.forEach((p) => {
    if (p.available !== false) availableCount++;
    else soldOutCount++;
    getProductTags(p).forEach((tag) => allTags.set(tag, (allTags.get(tag) || 0) + 1));
  });

  // ── Mobile ──
  const mContainer = document.getElementById('wishlist-mobile-filter-groups');
  if (mContainer) {
    let html = '';
    let idx = 1;

    // Availability
    let availItems = mobileCheckboxItem('wishlist-fm-available', 'availability', 'available', '在庫あり', availableCount);
    if (soldOutCount > 0) availItems += mobileCheckboxItem('wishlist-fm-soldout', 'availability', 'sold_out', '在庫切れ', soldOutCount);
    html += mobileDetails(idx++, '在庫状況', 'availability', `
      <ul class="facets-layout-list mobile-facets__list list-unstyled" role="list">${availItems}</ul>`);

    // Price range — preset checkboxes
    const priceItems = PRICE_RANGES.map((r) => `
      <li class="mobile-facets__item list-menu__item">
        <label for="wishlist-price-${r.id}" class="facets__label mobile-facets__label">
          <input class="mobile-facets__checkbox" type="checkbox" id="wishlist-price-${r.id}"
            data-group="price-range" data-gte="${r.gte ?? ''}" data-lte="${r.lte ?? ''}">
          <span class="mobile-facets__highlight"></span>
          ${SVG_CHECKBOX_F}${SVG_CHECKMARK_F}
          <span class="facet-checkbox__text" aria-hidden="true">${r.label}</span>
        </label>
      </li>`).join('');
    html += mobileDetails(idx++, '価格帯', 'price', `
      <ul class="facets-layout-list mobile-facets__list list-unstyled" role="list">${priceItems}</ul>`);

    // Tags — grouped by category
    const tagGroups = getTagGroups();
    if (tagGroups) {
      function buildTagGroupHtml(groupLabel, labelList, prefix) {
        const items = labelList
          .filter((name) => allTags.has(name))
          .map((name, i) => mobileCheckboxItem(`wishlist-fm-${prefix}-${i}`, 'tags', name, name, allTags.get(name)));
        if (items.length === 0) return '';
        return mobileDetails(idx++, groupLabel, 'tags', `
          <ul class="facets-layout-list mobile-facets__list list-unstyled" role="list">${items.join('')}</ul>`);
      }

      html += buildTagGroupHtml('ドレスライン', tagGroups.dressLine, 'dl');
      html += buildTagGroupHtml('ブランド', tagGroups.brand, 'br');
      html += buildTagGroupHtml('サイズ', tagGroups.size, 'sz');
      html += buildTagGroupHtml('アクセサリータイプ', tagGroups.accessoryType, 'at');
    }

    mContainer.innerHTML = html;
  }
}

// ─── Filter application ──────────────────────────────────────────────────────

function applyFiltersAndSort() {
  let filtered = allProducts.filter((p) => {
    if (filterState.availability.size > 0) {
      const isAvailable = p.available !== false;
      const matches =
        (filterState.availability.has('available') && isAvailable) ||
        (filterState.availability.has('sold_out') && !isAvailable);
      if (!matches) return false;
    }
    if (filterState.priceRanges.length > 0) {
      const union = computePriceUnion(filterState.priceRanges);
      const priceMin = union.gte !== null ? union.gte : 0;
      const priceMax = union.lte !== null ? union.lte : Infinity;
      const price = parseInt(p.variants[0]?.price || '0', 10);
      if (price < priceMin || price > priceMax) return false;
    }
    if (filterState.tags.size > 0) {
      const productTags = getProductTags(p);
      if (![...filterState.tags].some((t) => productTags.includes(t))) return false;
    }
    return true;
  });

  switch (filterState.sort) {
    case 'price-ascending':
      filtered.sort((a, b) => parseInt(a.variants[0]?.price || '0', 10) - parseInt(b.variants[0]?.price || '0', 10));
      break;
    case 'price-descending':
      filtered.sort((a, b) => parseInt(b.variants[0]?.price || '0', 10) - parseInt(a.variants[0]?.price || '0', 10));
      break;
    case 'title-ascending':
      filtered.sort((a, b) => a.title.localeCompare(b.title, 'ja'));
      break;
    case 'title-descending':
      filtered.sort((a, b) => b.title.localeCompare(a.title, 'ja'));
      break;
    case 'created-ascending':
      filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      break;
    case 'created-descending':
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      break;
    // 'manual' / 'best-selling' → wishlist registration order (no sort)
  }

  renderGrid(filtered);
  updateActivePills();
  updateProductCount(filtered.length, allProducts.length);
  syncCheckboxes();
}

function syncCheckboxes() {
  document.querySelectorAll('[data-group="availability"]').forEach((cb) => {
    cb.checked = filterState.availability.has(cb.value);
  });
  document.querySelectorAll('[data-group="tags"]').forEach((cb) => {
    cb.checked = filterState.tags.has(cb.value);
  });
  if (filterState.priceRanges.length === 0) {
    document.querySelectorAll('[data-group="price-range"]').forEach((cb) => { cb.checked = false; });
  } else {
    const union = computePriceUnion(filterState.priceRanges);
    // null は端値として扱う（lte=null → Infinity で全ての cbLte が通る）
    const numGteEff = union.gte !== null ? union.gte : 0;
    const numLteEff = union.lte !== null ? union.lte : Infinity;
    document.querySelectorAll('[data-group="price-range"]').forEach((cb) => {
      const cbGte = cb.dataset.gte === '' ? null : parseInt(cb.dataset.gte, 10);
      const cbLte = cb.dataset.lte === '' ? null : parseInt(cb.dataset.lte, 10);
      const cbGteEff = cbGte !== null ? cbGte : 0;
      const cbLteEff = cbLte !== null ? cbLte : Infinity;
      cb.checked = cbGteEff >= numGteEff && cbLteEff <= numLteEff;
    });
  }
}

function clearAllFilters() {
  filterState.availability.clear();
  filterState.priceRanges = [];
  filterState.tags.clear();
  applyFiltersAndSort();
}

function removeFilter(group, value) {
  if (group === 'availability') filterState.availability.delete(value);
  else if (group === 'price') { filterState.priceRanges = []; }
  else if (group === 'tags') filterState.tags.delete(value);
  applyFiltersAndSort();
}

// ─── Active pills ────────────────────────────────────────────────────────────

function buildPillsHtml() {
  let html = '';

  filterState.availability.forEach((value) => {
    const label = value === 'available' ? '在庫あり' : '在庫切れ';
    html += `<facet-remove>
      <a href="#" class="active-facets__button active-facets__button--light" role="button" data-remove-filter data-group="availability" data-value="${value}">
        <span class="active-facets__button-inner button button--tertiary">在庫状況: ${label}${SVG_CLOSE_SMALL_F}<span class="visually-hidden">フィルターを解除</span></span>
      </a>
    </facet-remove>`;
  });

  if (filterState.priceRanges.length > 0) {
    const union = computePriceUnion(filterState.priceRanges);
    const priceMin = union.gte !== null ? union.gte : 0;
    const priceMax = union.lte !== null ? union.lte : getMaxProductPrice();
    // コレクションページと同じフォーマット: "¥0 - ¥68,200,000"
    const priceLabel = `¥${priceMin.toLocaleString('ja-JP')} - ¥${priceMax.toLocaleString('ja-JP')}`;
    html += `<facet-remove>
      <a href="#" class="active-facets__button active-facets__button--light" role="button" data-remove-filter data-group="price" data-value="range">
        <span class="active-facets__button-inner button button--tertiary">価格帯: ${priceLabel}${SVG_CLOSE_SMALL_F}<span class="visually-hidden">フィルターを解除</span></span>
      </a>
    </facet-remove>`;
  }

  filterState.tags.forEach((tag) => {
    html += `<facet-remove>
      <a href="#" class="active-facets__button active-facets__button--light" role="button" data-remove-filter data-group="tags" data-value="${escapeHtml(tag)}">
        <span class="active-facets__button-inner button button--tertiary">詳細を絞り込む: ${escapeHtml(tag)}${SVG_CLOSE_SMALL_F}<span class="visually-hidden">フィルターを解除</span></span>
      </a>
    </facet-remove>`;
  });

  return html;
}

function updateActivePills() {
  ['wishlist-pills-mobile'].forEach((id) => {
    const container = document.getElementById(id);
    if (!container) return;
    // Remove previously injected pill elements (keep the clear-all facet-remove wrapper)
    container.querySelectorAll('facet-remove:not(.active-facets__button-wrapper)').forEach((el) => el.remove());
    // Insert new pills before the clear-all wrapper
    const clearWrapper = container.querySelector('.active-facets__button-wrapper');
    const pillsHtml = buildPillsHtml();
    if (pillsHtml && clearWrapper) {
      clearWrapper.insertAdjacentHTML('beforebegin', pillsHtml);
    }
  });
}

function updateProductCount(filtered, total) {
  const text = filtered === total ? `${total}件` : `${filtered}件 / ${total}件`;
  ['WishlistProductCountMobile'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  });
  const countText = `${filtered}個の商品`;
  const headerCount = document.getElementById('WishlistMobileFacetsCount');
  if (headerCount) headerCount.textContent = countText;
  const sortCount = document.getElementById('WishlistMobileSortCount');
  if (sortCount) sortCount.textContent = countText;
}

// ─── Grid rendering ──────────────────────────────────────────────────────────

function renderGrid(products) {
  const grid = document.getElementById('wishlist-grid');
  if (!grid) return;
  const toRender = products !== undefined ? products : allProducts;
  if (toRender.length === 0) {
    grid.innerHTML = `<li class="grid__item" style="grid-column:1/-1;text-align:center;padding:40px 0"><p>絞り込み条件に一致する商品はありません。</p></li>`;
    return;
  }
  grid.innerHTML = toRender.map(renderCard).join('');
}

function checkEmpty() {
  const container = document.getElementById('wishlist-container');
  const empty = document.getElementById('wishlist-empty');
  const facetsArea = document.getElementById('wishlist-facets-area');
  if (allProducts.length === 0) {
    if (container) container.hidden = true;
    if (empty) empty.hidden = false;
    if (facetsArea) facetsArea.hidden = true;
  }
}

// ─── Filter event setup ──────────────────────────────────────────────────────

function setupFilterEvents() {
  const area = document.getElementById('wishlist-facets-area');
  if (!area) return;

  area.addEventListener('change', (e) => {
    const el = e.target;
    if (el.matches('[data-group="availability"]')) {
      if (el.checked) filterState.availability.add(el.value);
      else filterState.availability.delete(el.value);
      applyFiltersAndSort();
    } else if (el.matches('[data-group="tags"]')) {
      if (el.checked) filterState.tags.add(el.value);
      else filterState.tags.delete(el.value);
      applyFiltersAndSort();
    } else if (el.matches('[data-group="price-range"]')) {
      // DOM のチェック済み状態からユニオンを計算（コレクションフィルターと同じ方式）
      filterState.priceRanges = [];
      area.querySelectorAll('[data-group="price-range"]:checked').forEach((cb) => {
        filterState.priceRanges.push({
          gte: cb.dataset.gte === '' ? null : parseInt(cb.dataset.gte, 10),
          lte: cb.dataset.lte === '' ? null : parseInt(cb.dataset.lte, 10),
        });
      });
      applyFiltersAndSort();
    }
  });

  const mobileSortApplyBtn = document.getElementById('WishlistMobileSortApplyBtn');
  if (mobileSortApplyBtn) {
    mobileSortApplyBtn.addEventListener('click', () => {
      const sel = document.getElementById('WishlistSortByMobile');
      if (!sel) return;
      filterState.sort = sel.value;
      const desktop = document.getElementById('WishlistSortBy');
      if (desktop) desktop.value = sel.value;
      applyFiltersAndSort();
      const wrapper = mobileSortApplyBtn.closest('.mobile-facets__wrapper');
      if (wrapper) wrapper.querySelector('summary')?.click();
    });
  }

  area.addEventListener('click', (e) => {
    // Mobile submenu open: add menu-opening class for slide-in animation
    const mobileSubSummary = e.target.closest('.mobile-facets__details > .mobile-facets__summary');
    if (mobileSubSummary) {
      const details = mobileSubSummary.parentElement;
      setTimeout(() => {
        if (details.hasAttribute('open')) {
          details.classList.add('menu-opening');
          const parent = details.closest('.has-submenu');
          if (parent) parent.classList.add('submenu-open');
        }
      }, 100);
      return;
    }

    // Mobile submenu close: animate slide-out before removing open
    const closeSubBtn = e.target.closest('[data-close-submenu]');
    if (closeSubBtn) {
      e.preventDefault();
      const details = closeSubBtn.closest('.mobile-facets__details');
      if (details) {
        const parent = details.closest('.submenu-open');
        if (parent) parent.classList.remove('submenu-open');
        details.classList.remove('menu-opening');
        setTimeout(() => details.removeAttribute('open'), 400);
      }
      return;
    }

    const resetBtn = e.target.closest('[data-reset]');
    if (resetBtn) {
      e.preventDefault();
      const group = resetBtn.dataset.reset;
      if (group === 'availability') filterState.availability.clear();
      else if (group === 'price') { filterState.priceMin = null; filterState.priceMax = null; }
      else if (group === 'tags') filterState.tags.clear();
      applyFiltersAndSort();
      return;
    }

    const removeEl = e.target.closest('[data-remove-filter]');
    if (removeEl) {
      e.preventDefault();
      removeFilter(removeEl.dataset.group, removeEl.dataset.value);
      return;
    }

    const clearEl = e.target.closest('#wishlist-clear-desktop, #wishlist-clear-mobile, #wishlist-clear-mobile-pills');
    if (clearEl) {
      e.preventDefault();
      clearAllFilters();
      return;
    }
  });

  // Close open dropdown on outside click (desktop)
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.facets__disclosure')) {
      document.querySelectorAll('#wishlist-facets-area .facets__disclosure[open]').forEach((d) => d.removeAttribute('open'));
    }
  });

  // Close other dropdowns when one opens (desktop)
  area.addEventListener('toggle', (e) => {
    if (e.target.matches('.facets__disclosure') && e.target.open) {
      document.querySelectorAll('#wishlist-facets-area .facets__disclosure[open]').forEach((d) => {
        if (d !== e.target) d.removeAttribute('open');
      });
    }
  }, true);
}

// ─── Init ────────────────────────────────────────────────────────────────────

async function init() {
  const wishlist = getWishlist();
  const loading = document.getElementById('wishlist-loading');
  const empty = document.getElementById('wishlist-empty');
  const container = document.getElementById('wishlist-container');

  if (wishlist.length === 0) {
    if (loading) loading.hidden = true;
    if (empty) empty.hidden = false;
    return;
  }

  const validItems = wishlist.filter((item) => item.handle);
  if (validItems.length === 0) {
    if (loading) loading.hidden = true;
    if (empty) empty.hidden = false;
    return;
  }

  const fetched = await Promise.all(validItems.map((item) => fetchProduct(item.handle)));
  allProducts = fetched.filter((p) => p !== null);

  if (loading) loading.hidden = true;

  if (allProducts.length === 0) {
    if (empty) empty.hidden = false;
    return;
  }

  if (container) container.hidden = false;

  const facetsArea = document.getElementById('wishlist-facets-area');
  if (facetsArea) facetsArea.hidden = false;

  buildFilters(allProducts);
  updateProductCount(allProducts.length, allProducts.length);
  setupFilterEvents();
  renderGrid(allProducts);

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.wishlist-btn');
    if (!btn) return;
    const grid = document.getElementById('wishlist-grid');
    if (!grid || !grid.contains(btn)) return;

    setTimeout(() => {
      const id = String(btn.dataset.productId);
      const still = getWishlist().some((item) => item.id === id);
      if (!still) {
        allProducts = allProducts.filter((p) => String(p.id) !== id);
        applyFiltersAndSort();
        checkEmpty();
      }
    }, 0);
  }, true);
}

document.addEventListener('DOMContentLoaded', init);
